import './styles.scoped.css'
import {useState, useEffect, Fragment} from 'react'
import {Typography, Button, IconButton, Menu, MenuItem, Avatar, Skeleton} from '@mui/material'
import {Add, MoreVert} from '@mui/icons-material'
import {useParams} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {caregiverApi} from '../../services'
import {parseError, getInitials, getColorFromName} from '../../utils/helpers'
import {RemoveServiceDialog, AssignService} from './components'
import {SkeletonContainer, TextSkeleton, AvatarSkeleton} from '../../components'

const ServiceCardsSkeleton = () => (
    <SkeletonContainer>
        {[0, 1].map(index => (
            <div key={index}>
                <br />
                <div className="card">
                    <div className="top">
                        <TextSkeleton width={index ? 150 : 190} fontSize={18} />
                        <Skeleton variant="circular" width={24} height={24} sx={{m: 1}} />
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Completed Schedule:
                        </Typography>
                        <TextSkeleton width={20} />
                        <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                            |
                        </Typography>
                        <Typography variant="h8" color="text.primary">
                            Active Schedule:
                        </Typography>
                        <TextSkeleton width={16} />
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Total Assigned Schedule:
                        </Typography>
                        <TextSkeleton width={20} />
                    </div>
                    <div className="content">
                        <Typography variant="h8" color="text.primary">
                            Clients:
                        </Typography>
                    </div>
                    <div className="content">
                        <AvatarSkeleton size={24} />
                        <TextSkeleton width={110} />
                        <AvatarSkeleton size={24} />
                        <TextSkeleton width={index ? 90 : 120} />
                    </div>
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default () => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [openAssignService, setOpenAssignService] = useState(false)
    const [openRemoveServiceDialog, setOpenRemoveServiceDialog] = useState(false)
    const [caregiverName, setCaregiverName] = useState(null)
    const [services, setServices] = useState([])
    const [selectedRow, setSelectedRow] = useState(null)
    const {caregiverId} = useParams()
    const [skeleton, setSkeleton] = useState(true)
    const {mobile} = useMobile()

    useEffect(() => {
        if (branchId) {
            caregiverApi
                .getBranchCaregiver(branchId, caregiverId)
                .then(({data}) => {
                    setCaregiverName(data?.first_name + ' ' + data?.last_name)
                })
                .catch(error => {
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })

            loadData(true)
        }
    }, [branchId])

    // The first load shows the skeleton; the reload after assigning or removing a service runs under the global spinner.
    const loadData = (initial = false) => {
        if (initial) {
            setSkeleton(true)
        } else {
            setLoading(true)
        }
        caregiverApi
            .getCaregiverServices(branchId, caregiverId)
            .then(({data}) => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                setServices(data?.services)
            })
            .catch(error => {
                if (!initial) setLoading(false)
                setSkeleton(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const assignService = () => {
        setOpenAssignService(true)
    }

    const removeService = () => {
        setOpenRemoveServiceDialog(true)
        menuClose()
    }

    return (
        <>
            {mobile ? (
                <Button variant="contained" startIcon={<Add />} fullWidth onClick={assignService}>
                    Assign Service
                </Button>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h6" color="text.primary">
                            Service
                        </Typography>
                        <Button variant="contained" startIcon={<Add />} onClick={assignService}>
                            Assign Service
                        </Button>
                    </div>
                </>
            )}
            <br />
            {skeleton && <ServiceCardsSkeleton />}
            {!skeleton &&
                services?.map((item, index) => (
                    <div key={index}>
                        <br />
                        <div className="card">
                            <div className="top">
                                <Typography variant="h7" color="primary.dark">
                                    {item?.service_name}
                                </Typography>
                                <IconButton onClick={e => menuOpen(e, item)}>
                                    <MoreVert />
                                </IconButton>
                            </div>
                            <div className="content">
                                <Typography variant="h8" color="text.primary">
                                    Completed Schedule:
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    {item?.schedule_stats?.completed_schedules ?? 0}
                                </Typography>
                                <Typography variant="body2" color="text.primary" style={{marginLeft: 12, marginRight: 12}}>
                                    |
                                </Typography>
                                <Typography variant="h8" color="text.primary">
                                    Active Schedule:
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    {item?.schedule_stats?.active_schedules ?? 0}
                                </Typography>
                            </div>
                            <div className="content">
                                <Typography variant="h8" color="text.primary">
                                    Total Assigned Schedule:
                                </Typography>
                                <Typography variant="body2" color="text.primary">
                                    {item?.schedule_stats?.total_schedules ?? 0}
                                </Typography>
                            </div>
                            <div className="content">
                                <Typography variant="h8" color="text.primary">
                                    Clients:
                                </Typography>
                            </div>
                            <div className="content" style={{flexWrap: 'wrap'}}>
                                {item?.clients?.length ? (
                                    item.clients.map(client => {
                                        const name = client?.first_name + ' ' + client?.last_name
                                        return (
                                            <Fragment key={client?.id}>
                                                <Avatar
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        fontSize: 12,
                                                        backgroundColor: getColorFromName(name),
                                                    }}>
                                                    {getInitials(name)}
                                                </Avatar>
                                                <Typography variant="body2" color="text.primary">
                                                    {name}
                                                </Typography>
                                            </Fragment>
                                        )
                                    })
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        -
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    <MenuItem onClick={removeService} sx={theme => ({color: theme.palette.error.main})}>
                        Remove Service
                    </MenuItem>
                </div>
            </Menu>
            <AssignService
                open={openAssignService}
                setOpen={setOpenAssignService}
                loadData={() => loadData()}
                oldServices={services}
                caregiverName={caregiverName}
            />
            <RemoveServiceDialog
                open={openRemoveServiceDialog}
                setOpen={setOpenRemoveServiceDialog}
                data={selectedRow}
                loadData={() => loadData()}
                caregiverName={caregiverName}
            />
        </>
    )
}
